import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client from "../../lib/api";
import { ALL_CATEGORIES } from "../../lib/categories";
import { toast } from "sonner";
import { UploadCloud, Trash2, Star, PlusCircle } from "lucide-react";

const EMPTY = {
  title: "", subtitle: "", bodyText: "", excerpt: "", category: "lucknow",
  location: "", state: "Uttar Pradesh", district: "", city: "", village: "", police_station: "",
  featured_image: "", image_credit: "", image_caption: "", image_alt: "", images: [],
  video_url: "", tagsText: "", language: "hi",
  author: "Rapid24News Editorial Desk", reporter: "", status: "published",
  breaking: false, featured: false, investigation: false, exclusive: false, editors_pick: false, trending: false,
  source_name: "", source_url: "", canonical_url: "", slug: "",
  seo_title: "", meta_description: "", editorial_note: "", published_at: "", allow_no_image: false, timeline: [],
};

const TIMELINE_STAGES = ["Incident", "Complaint / FIR", "Police Action", "Investigation", "Arrest", "Chargesheet", "Court", "Final Outcome"];

const isoToLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const localToIso = (local) => (local ? new Date(local).toISOString() : "");

export default function ArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [districts, setDistricts] = useState([]);
  const editing = !!id;
  const [dupWarn, setDupWarn] = useState(null);

  useEffect(() => { client.get("/districts").then((r) => setDistricts(r.data)); }, []);
  useEffect(() => {
    if (!id) { setForm(EMPTY); return; }
    client.get("/admin/articles", { params: { limit: 300 } }).then((r) => {
      const a = r.data.items.find((x) => x.id === id);
      if (a) client.get(`/articles/${a.slug}`).then((rr) => {
        const art = rr.data.article;
        setForm({
          ...EMPTY, ...art,
          bodyText: (art.body || []).join("\n\n"),
          tagsText: (art.tags || []).join(", "),
          published_at: isoToLocal(art.published_at),
          images: art.images || [],
          timeline: art.timeline || [],
        });
      });
    });
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (editing || !form.title || form.title.trim().length < 8) { setDupWarn(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await client.post("/admin/check-duplicate", { title: form.title, location: form.district });
        setDupWarn(r.data.duplicate ? r.data.matches : null);
      } catch { setDupWarn(null); }
    }, 700);
    return () => clearTimeout(t);
  }, [form.title, form.district, editing]);

  const uploadFeatured = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Image too large (max 3MB)."); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try { const r = await client.post("/admin/upload", { data: reader.result }); set("featured_image", r.data.url); toast.success("Featured image uploaded"); }
      catch { toast.error("Upload failed"); }
    };
    reader.readAsDataURL(file);
  };

  const addGalleryImage = () => setForm((f) => ({ ...f, images: [...f.images, { url: "", caption: "", attribution: "", alt: "" }] }));
  const setGallery = (i, k, v) => setForm((f) => { const imgs = [...f.images]; imgs[i] = { ...imgs[i], [k]: v }; return { ...f, images: imgs }; });
  const removeGallery = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));
  const uploadGallery = async (i, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Image too large (max 3MB)."); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try { const r = await client.post("/admin/upload", { data: reader.result }); setGallery(i, "url", r.data.url); toast.success("Image uploaded"); }
      catch { toast.error("Upload failed"); }
    };
    reader.readAsDataURL(file);
  };
  const makeFeatured = (img) => {
    set("featured_image", img.url);
    if (img.caption) set("image_caption", img.caption);
    if (img.attribution) set("image_credit", img.attribution);
    if (img.alt) set("image_alt", img.alt);
    toast.success("Set as featured image");
  };

  const addStage = () => setForm((f) => ({ ...f, timeline: [...(f.timeline || []), { label: "", date: "", note: "" }] }));
  const setStage = (i, k, v) => setForm((f) => { const t = [...f.timeline]; t[i] = { ...t[i], [k]: v }; return { ...f, timeline: t }; });
  const removeStage = (i) => setForm((f) => ({ ...f, timeline: f.timeline.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    const willPublish = form.status === "published" || form.status === "scheduled";
    if (willPublish && !(form.featured_image || "").trim() && !form.allow_no_image) {
      toast.error("Relevant image required before publication (or enable the controlled exception)."); return;
    }
    if (willPublish && form.category === "video" && !(form.video_url || "").trim()) {
      toast.error("A relevant video is required to publish a Video News story."); return;
    }
    setLoading(true);
    const payload = {
      ...form,
      body: form.bodyText.split("\n\n").map((p) => p.trim()).filter(Boolean),
      tags: form.tagsText.split(",").map((t) => t.trim()).filter(Boolean),
      images: (form.images || []).filter((im) => (im.url || "").trim()),
      timeline: (form.timeline || []).filter((t) => (t.label || t.note || t.date)),
      published_at: form.published_at ? localToIso(form.published_at) : null,
    };
    delete payload.bodyText; delete payload.tagsText; delete payload.id;
    delete payload.views; delete payload.created_at; delete payload.updated_at; delete payload.is_video; delete payload.author_slug; delete payload.seeded;
    try {
      if (editing) await client.put(`/admin/articles/${id}`, payload);
      else await client.post("/admin/articles", payload);
      toast.success(editing ? "Article updated" : "Article created");
      navigate("/admin/articles");
    } catch (err) { toast.error("Save failed: " + (err.response?.data?.detail || err.message)); }
    setLoading(false);
  };

  const Toggle = ({ k, label }) => (
    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`toggle-${k}`} className="w-4 h-4 accent-red-600" />
      <span className="font-head font-semibold uc text-xs">{label}</span>
    </label>
  );

  return (
    <div data-testid="article-editor">
      <h1 className="font-head font-bold text-3xl uc mb-6">{editing ? "Edit Article" : "New Article"}</h1>
      {dupWarn && (
        <div className="mb-5 bg-yellow-50 border-l-4 border-yellow-500 p-4" data-testid="duplicate-warning">
          <div className="font-head font-bold uc text-sm text-yellow-800">⚠ Possible Duplicate Story</div>
          <p className="text-sm text-gray-700 mt-1">A similar story already exists. Review before creating a separate article:</p>
          {dupWarn.map((m) => (
            <a key={m.slug} href={`/article/${m.slug}`} target="_blank" rel="noreferrer" className="block text-sm text-r24-red underline mt-1">{m.title} ({m.status})</a>
          ))}
        </div>
      )}
      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        {/* MAIN COLUMN */}
        <div className="space-y-4 bg-white border border-gray-200 p-5">
          <Input label="Headline *" value={form.title} onChange={(v) => set("title", v)} testid="editor-title" />
          <Input label="Subheadline" value={form.subtitle} onChange={(v) => set("subtitle", v)} testid="editor-subtitle" />
          <div>
            <label className="font-head font-semibold uc text-xs">Full Article (separate paragraphs with a blank line)</label>
            <textarea value={form.bodyText} onChange={(e) => set("bodyText", e.target.value)} rows={12} data-testid="editor-body"
              className="w-full border-2 border-gray-300 focus:border-r24-red outline-none px-3 py-2 mt-1 font-body" />
          </div>
          <Input label="Excerpt (optional, auto-generated if empty)" value={form.excerpt} onChange={(v) => set("excerpt", v)} testid="editor-excerpt" />
          <Input label="Tags (comma separated)" value={form.tagsText} onChange={(v) => set("tagsText", v)} testid="editor-tags" />
          <Input label="Editorial Note (for crime/allegation disclaimers)" value={form.editorial_note} onChange={(v) => set("editorial_note", v)} testid="editor-note" />

          {/* MULTIPLE IMAGES / GALLERY */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-head font-bold uc text-sm text-gray-500">Additional Images (Gallery)</h3>
              <button type="button" onClick={addGalleryImage} data-testid="editor-add-image" className="flex items-center gap-1 text-r24-red font-semibold text-xs uc"><PlusCircle size={15} /> Add Image</button>
            </div>
            {form.images.length === 0 && <p className="text-xs text-gray-400">No extra images. The featured image on the right is required for publishing.</p>}
            <div className="space-y-4">
              {form.images.map((im, i) => (
                <div key={i} className="border border-gray-200 p-3" data-testid={`gallery-image-${i}`}>
                  {im.url && <img src={im.url} alt={im.alt} className="w-full aspect-video object-cover mb-2" />}
                  <Input label="Image URL" value={im.url} onChange={(v) => setGallery(i, "url", v)} testid={`gallery-url-${i}`} />
                  <label className="flex items-center gap-2 justify-center border-2 border-dashed border-gray-300 py-2 my-2 cursor-pointer hover:border-r24-red text-xs font-semibold text-gray-600">
                    <UploadCloud size={15} /> Upload
                    <input type="file" accept="image/*" onChange={(e) => uploadGallery(i, e)} className="hidden" data-testid={`gallery-upload-${i}`} />
                  </label>
                  <div className="grid grid-cols-1 gap-2">
                    <Input label="Caption" value={im.caption} onChange={(v) => setGallery(i, "caption", v)} testid={`gallery-caption-${i}`} />
                    <Input label="Attribution / Credit" value={im.attribution} onChange={(v) => setGallery(i, "attribution", v)} testid={`gallery-attr-${i}`} />
                    <Input label="Alt Text" value={im.alt} onChange={(v) => setGallery(i, "alt", v)} testid={`gallery-alt-${i}`} />
                  </div>
                  <div className="flex gap-3 mt-2">
                    <button type="button" onClick={() => makeFeatured(im)} className="flex items-center gap-1 text-xs font-semibold text-black uc"><Star size={14} /> Set as Featured</button>
                    <button type="button" onClick={() => removeGallery(i)} data-testid={`gallery-remove-${i}`} className="flex items-center gap-1 text-xs font-semibold text-r24-red uc"><Trash2 size={14} /> Remove</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* STORY TIMELINE */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-head font-bold uc text-sm text-gray-500">Story Timeline (only verified developments)</h3>
              <button type="button" onClick={addStage} data-testid="editor-add-stage" className="flex items-center gap-1 text-r24-red font-semibold text-xs uc"><PlusCircle size={15} /> Add Stage</button>
            </div>
            {(!form.timeline || form.timeline.length === 0) && <p className="text-xs text-gray-400">No timeline stages. Add stages only when verified information exists (never fabricate events).</p>}
            <datalist id="timeline-stages">{TIMELINE_STAGES.map((s) => <option key={s} value={s} />)}</datalist>
            <div className="space-y-3">
              {(form.timeline || []).map((t, i) => (
                <div key={i} className="border border-gray-200 p-3" data-testid={`timeline-row-${i}`}>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="font-head font-semibold uc text-xs">Stage / Label</label>
                      <input list="timeline-stages" value={t.label || ""} onChange={(e) => setStage(i, "label", e.target.value)} data-testid={`timeline-label-${i}`}
                        className="w-full border-2 border-gray-300 focus:border-r24-red outline-none px-3 py-2 mt-1" />
                    </div>
                    <Input label="Date (e.g. 12 Jun 2026)" value={t.date} onChange={(v) => setStage(i, "date", v)} testid={`timeline-date-${i}`} />
                  </div>
                  <div className="mt-2">
                    <label className="font-head font-semibold uc text-xs">Verified detail</label>
                    <textarea value={t.note || ""} onChange={(e) => setStage(i, "note", e.target.value)} rows={2} data-testid={`timeline-note-${i}`}
                      className="w-full border-2 border-gray-300 focus:border-r24-red outline-none px-3 py-2 mt-1 font-body" />
                  </div>
                  <button type="button" onClick={() => removeStage(i)} data-testid={`timeline-remove-${i}`} className="flex items-center gap-1 text-xs font-semibold text-r24-red uc mt-2"><Trash2 size={14} /> Remove Stage</button>
                </div>
              ))}
            </div>
          </div>

          {/* SEO */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="font-head font-bold uc text-sm mb-3 text-gray-500">SEO &amp; URL</h3>
            <Input label="SEO Title" value={form.seo_title} onChange={(v) => set("seo_title", v)} testid="editor-seo-title" />
            <Input label="Meta Description" value={form.meta_description} onChange={(v) => set("meta_description", v)} testid="editor-meta" />
            <Input label="Slug (URL, auto from title if blank)" value={form.slug} onChange={(v) => set("slug", v)} testid="editor-slug" />
            <Input label="Canonical URL" value={form.canonical_url} onChange={(v) => set("canonical_url", v)} testid="editor-canonical" />
          </div>
        </div>

        {/* SIDEBAR */}
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 p-5 space-y-3">
            <button onClick={save} disabled={loading} data-testid="editor-save" className="w-full bg-r24-red text-white font-bold uc py-3 hover:bg-r24-reddark disabled:opacity-50">
              {loading ? "Saving…" : editing ? "Update" : "Publish"}
            </button>
            <Select label="Status" value={form.status} onChange={(v) => set("status", v)} options={[["published", "Published"], ["draft", "Draft"], ["scheduled", "Scheduled"], ["review", "Under Review"]]} testid="editor-status" />
            <div>
              <label className="font-head font-semibold uc text-xs">Publication Date &amp; Time</label>
              <input type="datetime-local" value={form.published_at} onChange={(e) => set("published_at", e.target.value)} data-testid="editor-datetime"
                className="w-full border-2 border-gray-300 focus:border-r24-red outline-none px-3 py-2 mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <Toggle k="breaking" label="Breaking" />
              <Toggle k="featured" label="Featured" />
              <Toggle k="investigation" label="Investigation Desk" />
              <Toggle k="editors_pick" label="Editor's Pick" />
              <Toggle k="trending" label="Trending" />
              <Toggle k="exclusive" label="Exclusive" />
            </div>
          </div>

          <div className="bg-white border border-gray-200 p-5 space-y-3">
            <Select label="Category" value={form.category} onChange={(v) => set("category", v)} options={ALL_CATEGORIES.map((c) => [c.slug, c.name])} testid="editor-category" />
            <Select label="Language" value={form.language} onChange={(v) => set("language", v)} options={[["hi", "Hindi"], ["en", "English"]]} testid="editor-language" />
            <Input label="State" value={form.state} onChange={(v) => set("state", v)} testid="editor-state" />
            <Select label="District" value={form.district} onChange={(v) => { set("district", v); if (!form.location) set("location", v); }} options={[["", "—"], ...districts.map((d) => [d, d])]} testid="editor-district" />
            <Input label="City" value={form.city} onChange={(v) => set("city", v)} testid="editor-city" />
            <Input label="Village" value={form.village} onChange={(v) => set("village", v)} testid="editor-village" />
            <Input label="Police Station" value={form.police_station} onChange={(v) => set("police_station", v)} testid="editor-police-station" />
            <Input label="Location (display)" value={form.location} onChange={(v) => set("location", v)} testid="editor-location" />
            <Input label="Reporter / Byline" value={form.reporter} onChange={(v) => set("reporter", v)} testid="editor-reporter" />
            <Input label="Author" value={form.author} onChange={(v) => set("author", v)} testid="editor-author" />
          </div>

          {/* FEATURED IMAGE */}
          <div className="bg-white border border-gray-200 p-5 space-y-3">
            <label className="font-head font-semibold uc text-xs">Featured Image *</label>
            {form.featured_image && (
              <div className="relative">
                <img src={form.featured_image} alt={form.image_alt} className="w-full aspect-video object-cover" data-testid="editor-featured-preview" />
                <button type="button" onClick={() => set("featured_image", "")} data-testid="editor-image-delete" className="absolute top-1 right-1 bg-r24-red text-white p-1" title="Delete image"><Trash2 size={14} /></button>
              </div>
            )}
            <Input label="Image URL" value={form.featured_image} onChange={(v) => set("featured_image", v)} testid="editor-image-url" />
            <label className="flex items-center gap-2 justify-center border-2 border-dashed border-gray-300 py-3 cursor-pointer hover:border-r24-red text-sm font-semibold text-gray-600">
              <UploadCloud size={18} /> Upload / Replace Image
              <input type="file" accept="image/*" onChange={uploadFeatured} className="hidden" data-testid="editor-image-upload" />
            </label>
            <Input label="Image Caption" value={form.image_caption} onChange={(v) => set("image_caption", v)} testid="editor-image-caption" />
            <Input label="Image Attribution / Credit" value={form.image_credit} onChange={(v) => set("image_credit", v)} testid="editor-image-credit" />
            <Input label="Image Alt Text" value={form.image_alt} onChange={(v) => set("image_alt", v)} testid="editor-image-alt" />
            <label className="flex items-center gap-2 cursor-pointer bg-yellow-50 border border-yellow-300 px-2 py-1.5">
              <input type="checkbox" checked={!!form.allow_no_image} onChange={(e) => set("allow_no_image", e.target.checked)} data-testid="editor-allow-no-image" className="w-4 h-4 accent-yellow-600" />
              <span className="text-[11px] text-yellow-800 font-semibold">Controlled exception: publish without an image</span>
            </label>
          </div>

          {/* VIDEO */}
          <div className="bg-white border border-gray-200 p-5 space-y-3">
            <label className="font-head font-semibold uc text-xs">Video News (YouTube URL / MP4)</label>
            {form.video_url ? (
              <div className="aspect-video bg-black">
                <iframe src={form.video_url} title="video" className="w-full h-full" allowFullScreen />
              </div>
            ) : <p className="text-[11px] text-gray-400">No video attached. No Video label or player will appear publicly unless a genuine video is added here.</p>}
            <Input label="Video URL" value={form.video_url} onChange={(v) => set("video_url", v)} testid="editor-video" />
            {form.video_url && (
              <button type="button" onClick={() => set("video_url", "")} data-testid="editor-video-delete" className="flex items-center gap-1 text-xs font-semibold text-r24-red uc"><Trash2 size={14} /> Delete Video</button>
            )}
          </div>

          <div className="bg-white border border-gray-200 p-5 space-y-3">
            <Input label="Source Name" value={form.source_name} onChange={(v) => set("source_name", v)} testid="editor-source-name" />
            <Input label="Source URL" value={form.source_url} onChange={(v) => set("source_url", v)} testid="editor-source-url" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="font-head font-semibold uc text-xs">{label}</label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full border-2 border-gray-300 focus:border-r24-red outline-none px-3 py-2 mt-1" />
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="font-head font-semibold uc text-xs">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full border-2 border-gray-300 focus:border-r24-red outline-none px-3 py-2 mt-1 bg-white">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
